/* 匯入的介面：全域落點覆蓋層、工作檯的選檔按鈕、待補標籤佇列。 */
import { useEffect, useRef, useState } from "react";
import type { Category, Concept } from "../domain/types.ts";
import { deriveTitle, NOTE_MIN_CHARS, validateConcept } from "../domain/rules.ts";
import { useI18n, localizedCategoryName } from "../i18n/index.tsx";
import { seedOriginalName } from "../domain/seed.ts";
import { Icon } from "./icons.tsx";

export type DropDest = "work" | "tray";

/** 拖曳時鋪滿整個視窗的兩個落點。手機沒有拖曳，所以同一條路徑也由選檔按鈕驅動。 */
export function ImportDropZone(props: { onFiles: (files: File[], dest: DropDest) => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [over, setOver] = useState<DropDest | null>(null);
  const depth = useRef(0);

  useEffect(() => {
    const enter = (e: DragEvent) => { e.preventDefault(); depth.current++; setOpen(true); };
    const over_ = (e: DragEvent) => {
      e.preventDefault();
      const el = e.target as HTMLElement | null;
      const zone = el?.closest?.("[data-drop]") as HTMLElement | null;
      setOver((zone?.dataset["drop"] as DropDest | undefined) ?? null);
    };
    const leave = () => { depth.current--; if (depth.current <= 0) { depth.current = 0; setOpen(false); setOver(null); } };
    const drop = (e: DragEvent) => {
      e.preventDefault();
      depth.current = 0;
      const el = e.target as HTMLElement | null;
      const zone = el?.closest?.("[data-drop]") as HTMLElement | null;
      const dest = (zone?.dataset["drop"] as DropDest | undefined) ?? "work";
      setOpen(false); setOver(null);
      const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      if (files.length) props.onFiles(files, dest);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", over_);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", over_);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
    };
  }, [props]);

  if (!open) return null;
  return (
    <div className="dropzone" data-open="true" data-import-dropzone>
      {(["work", "tray"] as DropDest[]).map((dest) => (
        <div key={dest} className="zone" data-drop={dest} data-over={over === dest}>
          <span className="zico"><Icon name={dest === "work" ? "export" : "peel"} /></span>
          <h3>{dest === "work" ? t("import.drop.work") : t("import.drop.tray")}</h3>
          <p>{dest === "work" ? t("import.drop.workHint") : t("import.drop.trayHint")}</p>
        </div>
      ))}
    </div>
  );
}

/** 選檔按鈕：與拖曳走同一條路徑。 */
export function ImportButton(props: { dest: DropDest; onFiles: (files: File[], dest: DropDest) => void; label?: string }) {
  const { t } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={input} type="file" multiple hidden data-import-input
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) props.onFiles(files, props.dest);
          e.target.value = "";
        }} />
      <button className="btn" onClick={() => input.current?.click()} data-import-pick>
        <Icon name="export" />{props.label ?? t("import.pick")}
      </button>
    </>
  );
}

export function DraftPanel(props: {
  drafts: Concept[]; categories: Category[];
  onComplete: (draft: Concept, note: string) => void;
  onDiscard: (draft: Concept) => void;
  onOpen: (draft: Concept) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const nameOf = (c: Category | undefined) => (c ? localizedCategoryName(c, t, seedOriginalName) : "—");
  const [notes, setNotes] = useState<Record<string, string>>({});
  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("import.drafts.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <p className="hint">{t("import.drafts.hint")}</p>
      {props.drafts.length === 0 && <p className="hint dim">{t("import.drafts.none")}</p>}
      {props.drafts.map((draft) => {
        const note = notes[draft.id] ?? "";
        const check = validateConcept({ summary: draft.summary, note, primaryCategoryId: draft.primaryCategoryId });
        return (
          <div className="panel" key={draft.id} data-draft={draft.id}>
            <h2 style={{ display: "block" }}>{draft.title || deriveTitle(draft.summary)}</h2>
            <p className="spec">{draft.summary}</p>
            <p className="hint mono">{t("import.drafts.from", { source: draft.source || "—" })}
              {"　"}{nameOf(props.categories.find((c) => c.id === draft.primaryCategoryId))}</p>
            <textarea aria-label={t("field.note.aria")} value={note}
              placeholder={t("field.note.placeholder")}
              onChange={(e) => setNotes((n) => ({ ...n, [draft.id]: e.target.value }))} />
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn primary" disabled={!check.ok} data-draft-complete={draft.id}
                onClick={() => props.onComplete(draft, note.trim())}>{t("import.drafts.complete")}</button>
              <button className="btn" onClick={() => props.onOpen(draft)}>{t("sheet.edit")}</button>
              <button className="btn" onClick={() => props.onDiscard(draft)}>{t("import.drafts.discard")}</button>
              {!check.ok && <span className="hint bad" style={{ margin: 0 }}>
                {check.errors[0] ? t(check.errors[0].code as never, check.errors[0].params) : ""}
                {check.errors[0]?.code === "error.note.tooShort"
                  ? "（至少 " + NOTE_MIN_CHARS + " 字）" : ""}</span>}
            </div>
          </div>
        );
      })}
    </section>
  );
}

/** 匯入進度：檔案多時讓使用者知道還在跑。 */
export function ImportProgress(props: { current: number; total: number; fileName: string }) {
  const { t } = useI18n();
  if (props.total === 0) return null;
  return (
    <div className="notice" data-import-progress>
      <Icon name="sync" />
      <span>{t("import.parsing")} {props.current} / {props.total}：{props.fileName}</span>
    </div>
  );
}
